import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { moduleConnectionStatus, CORE_MODULES } from "../../../constants/admin";
import { checkModuleHealth, getModuleEndpoints, registerModule } from "../api/moduleConnector";
import DatabaseSchemaGenerator from './database/DatabaseSchemaGenerator';
import { logError, logInfo } from '@utils/logger';
import { Button } from '../../../components/common/Button';
import '../styles.css';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [moduleStatus, setModuleStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [moduleEndpoints, setModuleEndpoints] = useState({});

  // Initialize admin module
  useEffect(() => {
    const initializeModule = async () => {
      try {
        // Register admin module
        await registerModule('admin', {
          baseUrl: window.location.origin,
          apiEndpoint: '/api/admin',
          importEndpoint: '/admin/import'
        });
        setInitialized(true);
        logInfo('Admin module initialized');
      } catch (error) {
        logError('Failed to initialize admin module:', error);
      }
    };

    initializeModule();
  }, []);

  // Sync data periodically
  useEffect(() => {
    if (!initialized) return;

    const syncData = async () => {
      try {
        // Push local changes to server
        const statusPromises = CORE_MODULES.map(async (module) => {
          const status = await checkModuleHealth(module);
          const endpoints = await getModuleEndpoints(module);
          return { module, status, endpoints };
        });

        const results = await Promise.all(statusPromises);
        
        const newStatus = {};
        const newEndpoints = {};
        results.forEach(({ module, status, endpoints }) => {
          newStatus[module] = status;
          newEndpoints[module] = endpoints;
        });

        setModuleStatus(newStatus);
        setModuleEndpoints(newEndpoints);
        setLoading(false);
        logInfo('Module status check complete');
      } catch (error) {
        logError('Error syncing survey:', error);
      }
    };

    // Initial sync
    syncData();

    // Set up interval
    const intervalId = setInterval(syncData, 30000); // Check every 30 seconds
    return () => clearInterval(intervalId);
  }, [initialized]);

  // Show loading state
  if (!user || loading) {
    return (
      <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center p-4">
        <LoadingSpinner />
        <div className="ml-3 text-white">Loading admin panel...</div>
      </div>
    );
  }

  // Check for admin access
  const isAdmin = user?.email === 'jfprince@soltec.ca' || 
                 user?.email === 'jfprince29@gmail.com' ||
                 user?.email === 'admin@oversizehub.net';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center p-4">
        <div className="bg-[#2A2B2E] p-6 rounded-lg text-center">
          <h2 className="text-xl font-semibold text-red-500 mb-2">Access Denied</h2>
          <p className="text-gray-400">Only administrators can access this panel.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const getStatusClass = (status) => {
    switch (status) {
      case moduleConnectionStatus.ACTIVE:
        return 'status-active';
      case moduleConnectionStatus.INACTIVE:
        return 'status-inactive';
      default:
        return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42] p-6">
      {/* Header */}
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-10 p-4 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">Admin Panel</h1>
            <p className="text-sm text-gray-400">System Management & Monitoring</p>
          </div>
        </div>
      </div>
      
      {/* API Management Section */}
      <section className="space-y-4">
        <h2 className="text-lg font-medium text-white mb-4">Module Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CORE_MODULES.map((module) => (
            <div key={module} className="bg-[#2A2B2E] p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium capitalize text-white">{module}</h3>
                <span className={`status-indicator ${getStatusClass(moduleStatus[module])}`}>
                  {moduleStatus[module] || 'Unknown'}
                </span>
              </div>
              <div className="text-sm text-gray-400">
                {moduleEndpoints[module] ? (
                  <ul className="list-disc list-inside">
                    {Object.entries(moduleEndpoints[module]).map(([key, value]) => (
                      <li key={key}>{key}: {value}</li>
                    ))}
                  </ul>
                ) : (
                  'No endpoints registered'
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Module Access Control */}
      <section className="space-y-4 mt-8">
        <h2 className="text-lg font-medium text-white mb-4">Module Access Control</h2>
        <div className="space-y-4">
          {CORE_MODULES.map((module) => (
            <div key={module} className="flex items-center justify-between">
              <span className="capitalize text-white">{module}</span>
              <button 
                className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30"
                onClick={() => checkModuleHealth(module)}
              >
                Update Connection
              </button>
            </div>
          ))}
        </div>
      </section>
      
      {/* Database Tools Section */}
      <section className="space-y-4 mt-8">
        <h2 className="text-lg font-medium text-white mb-4">Database Tools</h2>
        <DatabaseSchemaGenerator />
      </section>
      
      {/* Admin Tools Section */}
      <section className="space-y-4 mt-8">
        <h2 className="text-lg font-medium text-white mb-4">Admin Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#2A2B2E] p-4 rounded-lg">
            <h3 className="font-medium text-white mb-2">User Management</h3>
            <p className="text-sm text-gray-400 mb-4">Manage user accounts, roles, and permissions</p>
            <Button
              onClick={() => navigate('/admin/users')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Manage Users
            </Button>
          </div>
          
          <div className="bg-[#2A2B2E] p-4 rounded-lg">
            <h3 className="font-medium text-white mb-2">System Settings</h3>
            <p className="text-sm text-gray-400 mb-4">Configure system-wide settings and preferences</p>
            <Button
              onClick={() => navigate('/admin/settings')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              System Settings
            </Button>
          </div>
          
          <div className="bg-[#2A2B2E] p-4 rounded-lg">
            <h3 className="font-medium text-white mb-2">Import Data</h3>
            <p className="text-sm text-gray-400 mb-4">Import POIs from CSV files or photos</p>
            <Button
              onClick={() => navigate('/admin/import')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Import Manager
            </Button>
          </div>
        </div>
      </section>
      
      {/* Database Tools Section */}
      <section className="space-y-4 mt-8">
        <h2 className="text-lg font-medium text-white mb-4">Database Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#2A2B2E] p-4 rounded-lg">
            <h3 className="font-medium text-white mb-2">Hauteurs Libres</h3>
            <p className="text-sm text-gray-400 mb-4">Gérer les données de hauteurs libres du MTQ</p>
            <Button
              onClick={() => navigate('/admin/database/hauteurs-libres')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Gérer les Hauteurs Libres
            </Button>
          </div>
          
          <div className="bg-[#2A2B2E] p-4 rounded-lg">
            <h3 className="font-medium text-white mb-2">Structures</h3>
            <p className="text-sm text-gray-400 mb-4">Gérer les structures routières</p>
            <Button
              onClick={() => navigate('/admin/database/structures')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Gérer les Structures
            </Button>
          </div>
          
          <div className="bg-[#2A2B2E] p-4 rounded-lg">
            <h3 className="font-medium text-white mb-2">Couches WMS/WFS</h3>
            <p className="text-sm text-gray-400 mb-4">Gérer les couches cartographiques</p>
            <Button
              onClick={() => navigate('/admin/database/layers')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Gérer les Couches
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminPanel;